// ============================================
// FILE: /frontend/src/pages/WhatsAppToolsPage.jsx
// PURPOSE: WhatsApp Tools page - UPDATED description only
// NOTE: Import stays the same - BulkMessageGenerator now generates collages!
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Loader2, 
  ArrowLeft,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { listingsAPI } from '../api/listings.api';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import BulkMessageGenerator from '../components/whatsapp/BulkMessageGenerator';

export default function WhatsAppToolsPage() {
  console.log('[WhatsAppTools] 🎨 Component mounted');

  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchListings = async () => {
      if (!user) return;

      console.log('[WhatsAppTools] 🔵 Fetching user listings...');
      setLoading(true);

      try {
        const response = await listingsAPI.getUserListings(user.id);
        console.log('[WhatsAppTools] ✅ Listings fetched:', response.listings.length);

        const approvedListings = response.listings.filter(
          listing => listing.status === 'APPROVED'
        );

        console.log('[WhatsAppTools] 📊 Approved listings:', approvedListings.length);
        setListings(approvedListings);

      } catch (error) {
        console.error('[WhatsAppTools] ❌ Fetch error:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to load your listings'
        });
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchListings();
    }
  }, [user]);

  const handleMessageGenerated = (response) => {
    console.log('[WhatsAppTools] ✅ Collage generated callback:', {
      listingsCount: response.listingsCount,
      trackingIds: response.trackingIds?.length,
      timestamp: response.timestamp
    });

    // Optional: Track this event or show additional UI
  };

  if (authLoading || loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow">
          <div className="container py-12 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow">
          <div className="container py-12">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please <a href="/login" className="underline font-medium">login</a> to use WhatsApp tools.
              </AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow bg-muted/30">
        <div className="container py-6 sm:py-8 md:py-12 max-w-5xl">
          
          <div className="mb-6 sm:mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="mb-3 sm:mb-4 text-xs sm:text-sm font-medium hover:bg-accent"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
              Back to Dashboard
            </Button>

            <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold mb-1 sm:mb-2">
              WhatsApp Bulk Collage Generator
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Create a professional collage image showcasing up to 6 listings (2×3 grid)
            </p>
          </div>

          {listings.length === 0 && (
            <Alert className="mb-6 border-amber-200 bg-amber-50 text-amber-900">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-xs sm:text-sm">
                You don't have any approved listings yet. 
                <a href="/listings/create" className="underline font-medium ml-1 hover:text-amber-700">
                  Create a listing
                </a> to use this tool.
              </AlertDescription>
            </Alert>
          )}

          {listings.length > 0 && (
            <BulkMessageGenerator
              listings={listings}
              onMessageGenerated={handleMessageGenerated}
            />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}